import { Formik } from 'formik';
import { withZodSchema } from 'formik-validator-zod';
import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { postAffiliate } from 'src/api/pmApi';
import { DefaultTextField } from 'src/components/mui-overrides/Textfield';
import { awaitUtil } from 'src/util/utils';
import { z } from 'zod';
import CloseIcon from '@mui/icons-material/Close';
import LoadingButton from '@mui/lab/LoadingButton';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Switch,
  Typography,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import { NormalDatepicker } from '../mui-overrides/Datepicker';
import { MapsAutocomplete } from '../mui-overrides/MapsAutocomplete';

interface Props {
  open: boolean;
  handleClose: () => void;
  handleSave: () => void;
  partnerId: string;
}

const AffiliateSchema = z
  .object({
    externalAffiliate: z.boolean(),
    name: z.string().min(1),
    email: z.string().email(),
    address: z.string().min(1).optional(),
    birthDate: z.date().optional(),
  })
  .refine(
    (data) => {
      if (data.externalAffiliate === true) {
        if (!data.address || !data.birthDate) {
          return false;
        }
      }
      return true;
    },
    { message: '' },
  );

export const AddAffiliate: FC<Props> = ({ open, handleClose, handleSave, partnerId }) => {
  const { t } = useTranslation();
  const [sendingData, setSendingData] = useState(false);
  const [sendError, setSendError] = useState(false);
  const [externalAffiliate, setExternalAffiliate] = useState(false);

  const emptyAffiliate = {
    externalAffiliate: false,
    name: '',
    email: '',
    address: undefined,
    birthDate: undefined,
  };

  async function sendData(values: typeof emptyAffiliate) {
    setSendingData(true);
    const [result, error] = await awaitUtil(postAffiliate(partnerId, { ...values }));
    if (error) {
      setSendError(true);
      setSendingData(false);
    }
    if (result) {
      setSendingData(false);
      handleSave();
    }
  }

  const validator = (values: any) => {
    const closure = withZodSchema(AffiliateSchema);
    return closure(values);
  };

  const onClose = () => {
    setExternalAffiliate(false);
    setSendError(false);
    handleClose();
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth={true}>
        <DialogTitle>
          <Grid container>
            <Typography flexGrow={1} variant="h5" sx={{ fontWeight: 500 }}>
              {t('affiliate.add.modal.title')}
            </Typography>
            <IconButton
              onClick={() => {
                onClose();
              }}
            >
              <CloseIcon />
            </IconButton>
          </Grid>
        </DialogTitle>
        <Formik
          initialValues={emptyAffiliate}
          validate={validator}
          onSubmit={async (values) => {
            await sendData(values);
          }}
        >
          {(props) => {
            return (
              <>
                <DialogContent>
                  <DialogContentText>{t('affiliate.add.modal.subTitle')}</DialogContentText>
                  <form onSubmit={props.handleSubmit}>
                    <Grid container rowSpacing={0} columnSpacing={3}>
                      <FormControlLabel
                        onChange={() => {
                          setExternalAffiliate(!externalAffiliate);
                          props.setFieldValue('address', null);
                          props.setFieldValue('birthDate', null);
                          props.setFieldValue('externalAffiliate', !externalAffiliate);
                        }}
                        control={<Switch defaultChecked />}
                        label={t('affiliate.add.modal.form.label.internalAffiliate')}
                        value={externalAffiliate}
                      />
                      <DefaultTextField
                        label={t('affiliate.add.modal.form.label.name')}
                        placeholder={t('affiliate.add.modal.form.label.name')}
                        fieldName="name"
                        formik={props}
                      />

                      <DefaultTextField
                        label={t('affiliate.add.modal.form.label.email')}
                        placeholder={t('affiliate.add.modal.form.label.email')}
                        fieldName="email"
                        formik={props}
                      />
                      {externalAffiliate && (
                        <>
                          <div style={{ width: '100%', marginBottom: '16px' }}>
                            <MapsAutocomplete
                              label={t('affiliate.add.modal.form.label.address')}
                              field="address"
                              formik={props}
                              onAutocompleteSelected={(el) => {
                                props.setFieldValue('address', el.description);
                              }}
                            />
                          </div>
                          <NormalDatepicker label={t('affiliate.add.modal.form.label.birthDate')} fieldName="birthDate" formik={props} />
                        </>
                      )}
                    </Grid>
                  </form>
                  {sendError && <Alert severity="error">{t('affiliate.add.modal.error')}</Alert>}
                </DialogContent>
                <DialogActions>
                  <Button
                    variant="outlined"
                    onClick={() => {
                      onClose();
                    }}
                  >
                    {t('affiliate.add.modal.cancel')}
                  </Button>
                  <LoadingButton
                    variant="contained"
                    loading={sendingData}
                    onClick={props.submitForm}
                    disabled={Object.keys(props.errors).length !== 0}
                  >
                    {t('affiliate.add.modal.save')}
                  </LoadingButton>
                </DialogActions>
              </>
            );
          }}
        </Formik>
      </Dialog>
    </>
  );
};
